import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useOrgPermissions } from '@/hooks/useOrgPermissions'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { useToast } from '@/hooks/use-toast'
import {
  Search, Users, CheckCircle, XCircle, Clock, PauseCircle,
  FileText, AlertTriangle, Calendar, BookUser as PassportIcon,
  RotateCcw, UserCheck, UserX, ShieldAlert, ExternalLink,
} from 'lucide-react'

const STATUS_COLORS: Record<string, string> = {
  active:    'bg-emerald-100 text-emerald-800',
  invited:   'bg-blue-100 text-blue-800',
  suspended: 'bg-amber-100 text-amber-800',
  revoked:   'bg-red-100 text-red-700',
}

const STATUS_ICONS: Record<string, React.ReactNode> = {
  active:    <CheckCircle className="w-3.5 h-3.5" />,
  invited:   <Clock className="w-3.5 h-3.5" />,
  suspended: <PauseCircle className="w-3.5 h-3.5" />,
  revoked:   <XCircle className="w-3.5 h-3.5" />,
}

const PASSPORT_STATUS_COLORS: Record<string, string> = {
  valid:          'bg-emerald-100 text-emerald-700',
  expiring_soon:  'bg-amber-100 text-amber-700',
  expired:        'bg-red-100 text-red-700',
  none:           'bg-gray-100 text-gray-500',
  pending_upload: 'bg-blue-100 text-blue-700',
}

function passportDaysLeft(expiry: string | null): number | null {
  if (!expiry) return null
  const diff = new Date(expiry).getTime() - Date.now()
  return Math.ceil(diff / 86400000)
}

function PassportBadge({ passportStatus, passportExpiry }: { passportStatus: string | null; passportExpiry: string | null }) {
  const status = passportStatus ?? 'none'
  const days   = passportDaysLeft(passportExpiry)
  const labels: Record<string, string> = {
    valid:          `Valid${days != null ? ` · ${days}d` : ''}`,
    expiring_soon:  `Expiring in ${days}d`,
    expired:        'Expired',
    none:           'No passport',
    pending_upload: 'Awaiting upload',
  }
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${PASSPORT_STATUS_COLORS[status] ?? PASSPORT_STATUS_COLORS.none}`}>
      {status === 'expired' && <AlertTriangle className="w-3 h-3" />}
      {status === 'expiring_soon' && <Clock className="w-3 h-3" />}
      {labels[status] ?? status}
    </span>
  )
}

export function PressReviewView() {
  const { orgId } = useOrgPermissions()
  const { toast }  = useToast()
  const qc         = useQueryClient()

  const [search, setSearch]         = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterPassport, setPassport]   = useState('all')
  const [selected, setSelected]         = useState<any | null>(null)
  const [sheetOpen, setSheetOpen]       = useState(false)

  // Accredited journalists for this org — now includes passport fields
  const { data: accreditations = [], isLoading } = useQuery({
    queryKey: ['org-accreditations', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await (supabase
        .from('journalist_accreditations' as any)
        .select(`
          id, status, accredited_at, created_at,
          passport_url, passport_expiry, passport_country, passport_status,
          journalist_profiles(
            id, full_name, email, account_type, bio, phone, nationality,
            avatar_url, next_of_kin, emergency_contact, national_id_number,
            photo_status, photo_review_notes
          )
        `)
        .eq('org_id', orgId!)
        .order('created_at', { ascending: false }) as any)
      if (error) throw error
      return (data ?? []) as any[]
    },
  })

  // Submission counts per journalist
  const { data: submissionCounts = {} } = useQuery({
    queryKey: ['org-submission-counts', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await (supabase
        .from('journalist_submissions' as any)
        .select('journalist_id')
        .eq('org_id', orgId!) as any)
      const counts: Record<string, number> = {}
      ;(data ?? []).forEach((r: any) => {
        counts[r.journalist_id] = (counts[r.journalist_id] ?? 0) + 1
      })
      return counts
    },
  })

  const filtered = useMemo(() => {
    const q = search.toLowerCase()
    return accreditations.filter((a: any) => {
      const jp = a.journalist_profiles
      const matchSearch = !q ||
        (jp?.full_name ?? '').toLowerCase().includes(q) ||
        (jp?.email ?? '').toLowerCase().includes(q)
      const matchStatus  = filterStatus  === 'all' || a.status === filterStatus
      const matchPassport = filterPassport === 'all' || (a.passport_status ?? 'none') === filterPassport
      return matchSearch && matchStatus && matchPassport
    })
  }, [accreditations, search, filterStatus, filterPassport])

  const totalCount        = accreditations.length
  const activeCount       = accreditations.filter((a: any) => a.status === 'active').length
  const pendingCount      = accreditations.filter((a: any) => a.status === 'invited').length
  const suspendedCount    = accreditations.filter((a: any) => a.status === 'suspended').length
  const expiredPassport   = accreditations.filter((a: any) => a.passport_status === 'expired').length
  const expiringSoon      = accreditations.filter((a: any) => a.passport_status === 'expiring_soon').length

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await (supabase
        .from('journalist_accreditations' as any)
        .update({ status, accredited_at: status === 'active' ? new Date().toISOString() : undefined } as any)
        .eq('id', id) as any)
      if (error) throw error
    },
    onSuccess: (_, { status }) => {
      toast({ title: `Journalist ${status}.` })
      qc.invalidateQueries({ queryKey: ['org-accreditations', orgId] })
      if (selected) setSelected((prev: any) => ({ ...prev, status }))
    },
    onError: (e: any) => toast({ title: 'Failed', description: e.message, variant: 'destructive' }),
  })

  const updatePhotoStatus = useMutation({
    mutationFn: async ({ journalistId, photo_status, notes }: { journalistId: string; photo_status: 'approved' | 'reupload_requested'; notes?: string }) => {
      const { data: { user } } = await supabase.auth.getUser()
      const payload: any = { photo_status }
      if (photo_status === 'approved') {
        payload.photo_reviewed_by = user?.id ?? null
        payload.photo_reviewed_at = new Date().toISOString()
        payload.photo_review_notes = null
      } else {
        payload.photo_reupload_requested_by = user?.id ?? null
        payload.photo_reupload_requested_at = new Date().toISOString()
        payload.photo_review_notes = notes || null
      }
      const { error } = await (supabase.from('journalist_profiles' as any).update(payload).eq('id', journalistId) as any)
      if (error) throw error
    },
    onSuccess: (_, { photo_status }) => {
      toast({ title: photo_status === 'approved' ? 'Photo approved.' : 'New photo requested.' })
      qc.invalidateQueries({ queryKey: ['org-accreditations', orgId] })
      if (selected) setSelected((prev: any) => ({ ...prev, journalist_profiles: { ...prev.journalist_profiles, photo_status } }))
    },
    onError: (e: any) => toast({ title: 'Failed', description: e.message, variant: 'destructive' }),
  })

  const openSheet = (a: any) => { setSelected(a); setSheetOpen(true) }

  const statusAction = (a: any) => {
    if (a.status === 'invited') {
      return (
        <div className="flex gap-1">
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
            onClick={e => { e.stopPropagation(); updateStatus.mutate({ id: a.id, status: 'active' }) }}>
            <UserCheck className="w-3 h-3" /> Accept
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-red-700 border-red-200 hover:bg-red-50"
            onClick={e => { e.stopPropagation(); updateStatus.mutate({ id: a.id, status: 'revoked' }) }}>
            <UserX className="w-3 h-3" /> Reject
          </Button>
        </div>
      )
    }
    if (a.status === 'active') {
      return (
        <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-amber-700 border-amber-200 hover:bg-amber-50"
          onClick={e => { e.stopPropagation(); updateStatus.mutate({ id: a.id, status: 'suspended' }) }}>
          <PauseCircle className="w-3 h-3" /> Suspend
        </Button>
      )
    }
    if (a.status === 'suspended') {
      return (
        <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
          onClick={e => { e.stopPropagation(); updateStatus.mutate({ id: a.id, status: 'active' }) }}>
          <RotateCcw className="w-3 h-3" /> Reinstate
        </Button>
      )
    }
    return null
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-[#0D2240]">Press Review</h2>
        <p className="text-justify text-sm text-muted-foreground mt-0.5">
          Manage all accredited journalists. Accept, suspend, or revoke press access. Passport validity is tracked automatically.
        </p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: 'Total',          value: totalCount,     icon: Users,         color: 'text-[#0D2240]',    bg: 'bg-[#F7F5F0]' },
          { label: 'Active',         value: activeCount,    icon: CheckCircle,   color: 'text-emerald-600',  bg: 'bg-emerald-50' },
          { label: 'Pending',        value: pendingCount,   icon: Clock,         color: 'text-blue-600',     bg: 'bg-blue-50' },
          { label: 'Suspended',      value: suspendedCount, icon: PauseCircle,   color: 'text-amber-600',    bg: 'bg-amber-50' },
          { label: 'Passport Exp.',  value: expiredPassport, icon: ShieldAlert,  color: 'text-red-600',      bg: 'bg-red-50' },
          { label: 'Exp. Soon',      value: expiringSoon,   icon: AlertTriangle, color: 'text-amber-600',    bg: 'bg-amber-50' },
        ].map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className={`${bg} border border-[#DDD8CE] rounded-xl p-3 flex items-center gap-2`}>
            <Icon className={`w-4 h-4 shrink-0 ${color}`} />
            <div>
              <div className="text-lg font-bold text-[#0D2240] leading-none">{value}</div>
              <div className="text-[10px] text-muted-foreground mt-0.5">{label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            placeholder="Search journalist..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-8 h-8 text-sm w-56"
          />
        </div>

        <div className="flex gap-1">
          {['all', 'active', 'invited', 'suspended', 'revoked'].map(s => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={[
                'px-2.5 py-1 rounded-full text-xs font-medium border transition-colors',
                filterStatus === s
                  ? 'bg-[#0D2240] text-white border-[#0D2240]'
                  : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400',
              ].join(' ')}
            >
              {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        <div className="flex gap-1">
          {['all', 'expired', 'expiring_soon', 'none'].map(s => (
            <button
              key={s}
              onClick={() => setPassport(s)}
              className={[
                'px-2.5 py-1 rounded-full text-xs font-medium border transition-colors',
                filterPassport === s
                  ? 'bg-red-600 text-white border-red-600'
                  : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400',
              ].join(' ')}
            >
              {s === 'all' ? 'All Passports' : s === 'expiring_soon' ? 'Expiring Soon' : s === 'none' ? 'No Passport' : 'Expired'}
            </button>
          ))}
        </div>

        <div className="ml-auto text-xs text-muted-foreground">
          {filtered.length} journalist{filtered.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow style={{ backgroundColor: '#0D2240' }}>
              {['Journalist', 'Type', 'Submissions', 'Passport', 'Status', 'Since', 'Actions'].map(h => (
                <TableHead key={h} style={{ color: '#F0E4B8' }} className="font-semibold">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">Loading...</TableCell>
              </TableRow>
            )}
            {!isLoading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                  No journalists match the current filter.
                </TableCell>
              </TableRow>
            )}
            {filtered.map((a: any, idx: number) => {
              const jp = a.journalist_profiles
              const submCount = submissionCounts[jp?.id] ?? 0
              return (
                <TableRow
                  key={a.id}
                  className={`cursor-pointer hover:bg-blue-50 ${idx % 2 === 1 ? 'bg-[#F7F5F0]' : ''}`}
                  onClick={() => openSheet(a)}
                >
                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      {jp?.avatar_url ? (
                        <img src={jp.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-[#0D2240] flex items-center justify-center text-[#F0E4B8] text-[11px] font-bold shrink-0">
                          {(jp?.full_name ?? '?').split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium">{jp?.full_name ?? 'Unknown'}</p>
                        <p className="text-xs text-muted-foreground">{jp?.email ?? ''}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize text-[10px]">
                      {jp?.account_type ?? 'individual'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="flex items-center gap-1 text-sm">
                      <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                      {submCount}
                    </span>
                  </TableCell>
                  <TableCell>
                    <PassportBadge passportStatus={a.passport_status} passportExpiry={a.passport_expiry} />
                  </TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_COLORS[a.status] ?? 'bg-zinc-100 text-zinc-600'}`}>
                      {STATUS_ICONS[a.status]}
                      {a.status.charAt(0).toUpperCase() + a.status.slice(1)}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {a.accredited_at
                      ? new Date(a.accredited_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                      : new Date(a.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </TableCell>
                  <TableCell onClick={e => e.stopPropagation()}>
                    {statusAction(a)}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>

      {/* Detail sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="right" className="w-[440px] overflow-y-auto">
          {selected && (() => {
            const jp        = selected.journalist_profiles
            const submCount = submissionCounts[jp?.id] ?? 0
            const days      = passportDaysLeft(selected.passport_expiry)
            return (
              <>
                <SheetHeader className="mb-6">
                  <div className="flex items-center gap-3">
                    {jp?.avatar_url ? (
                      <img src={jp.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-[#0D2240] flex items-center justify-center text-[#F0E4B8] text-sm font-bold">
                        {(jp?.full_name ?? '?').split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <SheetTitle className="text-[#0D2240]">{jp?.full_name ?? 'Unknown'}</SheetTitle>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium mt-1 ${STATUS_COLORS[selected.status] ?? ''}`}>
                        {STATUS_ICONS[selected.status]}
                        {selected.status.charAt(0).toUpperCase() + selected.status.slice(1)}
                      </span>
                    </div>
                  </div>
                </SheetHeader>

                <div className="space-y-5">
                  {/* Info grid */}
                  <div className="grid grid-cols-2 gap-3">
                    <InfoCell label="Email"       value={jp?.email ?? '—'} />
                    <InfoCell label="Phone"       value={jp?.phone ?? '—'} />
                    <InfoCell label="Type"        value={jp?.account_type ?? 'individual'} />
                    <InfoCell label="Nationality" value={jp?.nationality ?? '—'} />
                    <InfoCell label="National ID / passport no." value={jp?.national_id_number ?? '—'} />
                    <InfoCell label="Submissions" value={String(submCount)} />
                    <InfoCell label="Accredited"  value={selected.accredited_at ? new Date(selected.accredited_at).toLocaleDateString('en-GB') : 'Pending'} />
                  </div>

                  {/* Emergency contact */}
                  <div className="border rounded-xl p-4 space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                      <ShieldAlert className="w-3.5 h-3.5" /> Emergency contact
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <InfoCell label="Next of kin" value={jp?.next_of_kin ?? '—'} />
                      <InfoCell label="Emergency contact" value={jp?.emergency_contact ?? '—'} />
                    </div>
                  </div>

                  {/* Photo KYC section */}
                  <div className="border rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Photo (KYC)</p>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${
                        jp?.photo_status === 'approved' ? 'bg-emerald-100 text-emerald-800'
                        : jp?.photo_status === 'reupload_requested' ? 'bg-amber-100 text-amber-800'
                        : 'bg-gray-100 text-gray-600'
                      }`}>
                        {(jp?.photo_status ?? 'pending').replace('_', ' ')}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      {jp?.avatar_url ? (
                        <img src={jp.avatar_url} alt="" className="w-16 h-16 rounded-lg object-cover border" />
                      ) : (
                        <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400 text-xs">No photo</div>
                      )}
                      <p className="text-[11px] text-muted-foreground flex-1">
                        Must be a passport-style photo on a plain white background.
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {jp?.photo_status !== 'approved' && jp?.avatar_url && (
                        <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                          onClick={() => updatePhotoStatus.mutate({ journalistId: jp.id, photo_status: 'approved' })}
                          disabled={updatePhotoStatus.isPending}>
                          <UserCheck className="w-3 h-3" /> Approve photo
                        </Button>
                      )}
                      {jp?.photo_status !== 'reupload_requested' && (
                        <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-amber-700 border-amber-200 hover:bg-amber-50"
                          onClick={() => {
                            const notes = window.prompt('Optional note for the journalist about why a new photo is needed:') ?? undefined
                            updatePhotoStatus.mutate({ journalistId: jp.id, photo_status: 'reupload_requested', notes })
                          }}
                          disabled={updatePhotoStatus.isPending}>
                          <RotateCcw className="w-3 h-3" /> Request new photo
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Passport section */}
                  <div className="border rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Passport</p>
                      <PassportBadge passportStatus={selected.passport_status} passportExpiry={selected.passport_expiry} />
                    </div>

                    {selected.passport_status === 'expired' && (
                      <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">
                        <AlertTriangle className="w-4 h-4 shrink-0" />
                        Passport expired {days !== null ? `${Math.abs(days)} days ago` : ''}. Journalist has been notified to upload a new one.
                      </div>
                    )}
                    {selected.passport_status === 'expiring_soon' && (
                      <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-700">
                        <Clock className="w-4 h-4 shrink-0" />
                        Passport expires in {days} day{days !== 1 ? 's' : ''}. Journalist has been notified.
                      </div>
                    )}
                    {selected.passport_status === 'none' && (
                      <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-600">
                        <Calendar className="w-4 h-4 shrink-0" />
                        No passport uploaded yet.
                      </div>
                    )}

                    {selected.passport_expiry && (
                      <div className="flex items-center gap-1.5 text-sm">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Expiry:</span>
                        <span className="font-medium">{new Date(selected.passport_expiry).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                      </div>
                    )}
                    {selected.passport_country && (
                      <p className="text-sm text-muted-foreground">Country: <span className="font-medium text-foreground">{selected.passport_country}</span></p>
                    )}
                    {selected.passport_url && (
                      <a
                        href={selected.passport_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                      >
                        <ExternalLink className="w-4 h-4" />
                        View passport scan
                      </a>
                    )}
                  </div>

                  {jp?.bio && (
                    <div>
                      <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5">Bio</div>
                      <p className="text-justify text-sm leading-relaxed">{jp.bio}</p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="border-t border-border pt-4 space-y-2">
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Actions</div>
                    {selected.status === 'invited' && (
                      <div className="flex gap-2">
                        <Button className="flex-1 gap-1.5 bg-[#006B3C] hover:bg-[#005A32] text-white"
                          onClick={() => { updateStatus.mutate({ id: selected.id, status: 'active' }); setSheetOpen(false) }}>
                          <UserCheck className="w-4 h-4" /> Accept Journalist
                        </Button>
                        <Button variant="outline" className="flex-1 gap-1.5 text-red-700 border-red-200 hover:bg-red-50"
                          onClick={() => { updateStatus.mutate({ id: selected.id, status: 'revoked' }); setSheetOpen(false) }}>
                          <UserX className="w-4 h-4" /> Reject
                        </Button>
                      </div>
                    )}
                    {selected.status === 'active' && (
                      <Button variant="outline" className="w-full gap-1.5 text-amber-700 border-amber-200 hover:bg-amber-50"
                        onClick={() => { updateStatus.mutate({ id: selected.id, status: 'suspended' }); setSheetOpen(false) }}>
                        <PauseCircle className="w-4 h-4" /> Suspend Access
                      </Button>
                    )}
                    {selected.status === 'suspended' && (
                      <Button className="w-full gap-1.5 bg-[#006B3C] hover:bg-[#005A32] text-white"
                        onClick={() => { updateStatus.mutate({ id: selected.id, status: 'active' }); setSheetOpen(false) }}>
                        <RotateCcw className="w-4 h-4" /> Reinstate Access
                      </Button>
                    )}
                    {selected.status !== 'revoked' && selected.status !== 'invited' && (
                      <Button variant="outline" className="w-full gap-1.5 text-red-700 border-red-200 hover:bg-red-50"
                        onClick={() => { updateStatus.mutate({ id: selected.id, status: 'revoked' }); setSheetOpen(false) }}>
                        <XCircle className="w-4 h-4" /> Revoke Press Access
                      </Button>
                    )}
                  </div>
                </div>
              </>
            )
          })()}
        </SheetContent>
      </Sheet>
    </div>
  )
}

function InfoCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-[#F7F5F0] rounded-lg p-3">
      <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">{label}</div>
      <div className="text-sm font-medium capitalize">{value}</div>
    </div>
  )
}
